import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// The checks page describing its own filter bar. `filterByDateType` is the page's own switch between
// check date and deposit date, so a request about deposits changes what the range means rather than
// silently filtering the wrong column.
declareAiCapability({
    id: "checks.filter",
    title: "Filter the checks list",
    effect: "prefill",
    description:
        "Sets the filters on Billing > Checks and re-runs its search: the date range and whether it means the check date or the deposit date, a check number, the smallest and largest amount, contract and office, plus a free-text search over the table. Anything left out keeps what the page already has. Only works while that page is open.",
    location: "the filter bar of the Billing > Checks page",
    scope: "page",
    permissionKey: "view_billing_section_checks",
    params: [
        { name: "from", type: "date", required: false, description: "First day to include, as yyyy-MM-dd." },
        { name: "to", type: "date", required: false, description: "Last day to include, as yyyy-MM-dd." },
        {
            name: "dateBasis",
            type: "enum",
            required: false,
            description: "Which date the range means. Use deposit when the request talks about deposits.",
            options: [
                { value: "CHECK_DATE", label: "Check date" },
                { value: "DEPOSIT_DATE", label: "Deposit date" }
            ]
        },
        { name: "checkNumber", type: "text", required: false, description: "A single check number." },
        { name: "minAmount", type: "number", required: false, description: "Smallest check amount, in dollars." },
        { name: "maxAmount", type: "number", required: false, description: "Largest check amount, in dollars." },
        {
            name: "contract",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getChecksContractOptions(),
            description: "Limit to these contracts."
        },
        {
            name: "office",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getChecksOfficeOptions(),
            description: "Limit to these offices."
        },
        {
            name: "search",
            type: "text",
            required: false,
            searchesFreeText: true,
            description: "Free text the table searches over every column."
        }
    ],
    examples: [
        "checks deposited last month",
        "checks over 5000 for the france contract",
        "check number 1042"
    ]
});

// Agency data, read when the manifest is built rather than when this file is imported.
let checksContractTypes = () => [];
let checksOffices = () => [];

function getChecksContractOptions() {
    return checksContractTypes().map((contract) => ({ value: String(contract.id), label: contract.name }));
}

function getChecksOfficeOptions() {
    return checksOffices().map((office) => ({ value: String(office.id), label: office.name }));
}

//! @ngInject
export function billingChecksCtrl(
    $scope,
    $rootScope,
    DatabaseApi,
    NgTableParams,
    itemSearchPageManager,
    $state,
    toaster,
    $uibModal,
    FilterUtils,
    $filter,
    SweetAlert,
    BillingChecksService,
    selectionLogic,
    wildcard
) {

    $scope.tableParams = null;
    $scope.globalFilter = { val: "" };

    function initialize() {
        $scope.offices = DatabaseApi.offices() || {};
        $scope.officesOptions = $scope.offices.map(office => ({ id: office.id, label: office.name }));
        initPageManager();
        $scope.pageManager.resetSearchParamsByUrl();

        // Load filters
        const storageFilters = FilterUtils.rootFilters.billingChecksCtrl;
        if (storageFilters !== undefined) {
            $scope.pageManager.setSearchParams(storageFilters);
        }
    }

    const initSelection = function (items) {
        $scope.selectionLogic = selectionLogic.createNewLogic(function (item) {
            if (!item.exported) $scope.selectionLogic.addItemToCollection(item);
        }, "id");
        if (items) {
            items.forEach((item) => {
                $scope.selectionLogic.initItem(item);
            });
        }
    };

    $scope.$on("$destroy", function () {
        const filters = $scope.pageManager.searchParams;
        FilterUtils.rootFilters.billingChecksCtrl = angular.copy(filters);
    });

    var initPageManager = function () {
        $scope.pageManager = itemSearchPageManager.createSearchPageManager("/check_batches");

        $scope.contractTypesDataManager = $scope.pageManager.getContractTypesDataManager();
        $scope.officeDataManager = $scope.pageManager.getOfficeDataManager();

        $scope.pageManager.initFromToDateParams();
        $scope.pageManager.initSearchParam("checkNumber", "", { placeholderValue: "", isEntity: true });
        $scope.pageManager.initSearchParam("filterByDateType", "CHECK_DATE", {}); // enum: 'CHECK_DATE' and 'DEPOSIT_DATE'
        $scope.pageManager.initSearchParam("minAmount", "");
        $scope.pageManager.initSearchParam("maxAmount", "");
        $scope.pageManager.initContractTypesParam();
        $scope.pageManager.initOfficeParam();
    };


    checksContractTypes = () => $scope.contractTypesDataManager?.getActiveContractTypes() ?? [];
    checksOffices = () => $scope.officeDataManager?.getOffices() ?? [];

    const releaseChecksFilter = provideAiCapability("checks.filter", (args) => {
        const applied = [];
        const asList = (value) => (Array.isArray(value) ? value : [value]);
        const asDate = (text) => {
            const parts = String(text).split("-").map(Number);

            if (parts.length !== 3 || parts.some(isNaN)) {
                throw new Error("Could not read the date " + text + ".");
            }

            return new Date(parts[0], parts[1] - 1, parts[2]);
        };
        const pickAll = (available, requested, what) =>
            asList(requested).map((one) => {
                const found = (available ?? []).find((item) => String(item.id) === String(one));

                if (found === undefined) {
                    throw new Error("This agency has no " + what + " " + one + ".");
                }

                return found;
            });

        if (args.dateBasis !== undefined) {
            const basis = String(args.dateBasis).toUpperCase();

            if (basis !== "CHECK_DATE" && basis !== "DEPOSIT_DATE") {
                throw new Error("The date range is either by check date or by deposit date.");
            }

            $scope.pageManager.searchParams.filterByDateType.value = basis;
            applied.push(basis === "DEPOSIT_DATE" ? "by deposit date" : "by check date");
        }

        if (args.from !== undefined) {
            $scope.pageManager.searchParams.from.value = asDate(args.from);
        }

        if (args.to !== undefined) {
            $scope.pageManager.searchParams.to.value = asDate(args.to);
        }

        if (args.from !== undefined || args.to !== undefined) {
            applied.push([args.from, args.to].filter(Boolean).join(" to "));
        }

        for (const [name, label] of [["checkNumber", "check"], ["minAmount", "from"], ["maxAmount", "up to"]]) {
            if (args[name] !== undefined) {
                $scope.pageManager.searchParams[name].value = String(args[name]);
                applied.push(label + " " + String(args[name]));
            }
        }

        if (args.contract !== undefined) {
            const contracts = pickAll(checksContractTypes(), args.contract, "contract");

            $scope.pageManager.searchParams.selectedContractTypes.value = contracts;
            applied.push(contracts.map((contract) => contract.name).join(" and "));
        }

        if (args.office !== undefined) {
            const offices = pickAll(checksOffices(), args.office, "office");

            $scope.pageManager.searchParams.selectedOffices.value = offices;
            applied.push(offices.map((office) => office.name).join(" and "));
        }

        if (args.search !== undefined) {
            $scope.globalFilter.val = String(args.search);
            applied.push('"' + String(args.search) + '"');
        }

        $scope.loadItems();

        if (args.search !== undefined) {
            $scope.applyGlobalSearch($scope.globalFilter.val);
        }

        return applied.length === 0
            ? "Re-ran the checks search"
            : "Showing checks: " + applied.join(", ");
    });

    $scope.$on("$destroy", releaseChecksFilter);

    var mapItems = function (items) {
        items.forEach(function (item) {
            if (!item.contractTypeName && item.contractTypeId) {
                var contractType = $scope.contractTypesDataManager.getContractTypeById(item.contractTypeId);
                item.contractTypeName = (contractType && contractType.name) || item.contractTypeId;
            }

            if (!item.officesNames && item.officesIds) {
                item.officesNames = $scope.offices.filter(officeItem => item.officesIds.includes(officeItem.id)).map(officeItem => officeItem.name).join(", ");
            }
        });
    };

    const initTable = (items) => {
        const oldTotal = $scope.tableParams?.total?.() || 0;
        if (!items) {
            toaster.pop("error", "Oops... Something went wrong");
            return;
        }
        if (!items.length) {
            return;
        }
        mapItems(items);
        initSelection(items);

        let page = false;
        let sorting = {};
        let count = 25;
        if ($scope.tableParams) {
            page = $scope.tableParams.page();
            sorting = $scope.tableParams.sorting();
            count = $scope.tableParams.count();
        }

        const options = {
            sorting: sorting,
            count: count
        };
        $scope.tableParams = new NgTableParams(options, {
            dataset: items
        });
        if (page && oldTotal === $scope.tableParams.total()) $scope.tableParams.page(page);
    };

    $scope.applyGlobalSearch = function (term) {
        var filter = { $: term };
        if ($scope.tableParams) {
            angular.extend($scope.tableParams.filter(), filter);
        }
    };

    $scope.loadItems = function () {
        $scope.pageManager.executeSearch().then(function (response) {
            $scope.allChecks = [...response.data.checkBatches];
            initTable(response.data.checkBatches);
        }, function (error) {
            toaster.pop("error", "Failed to load check batches");
        });
    };

    $scope.clickTableRow = function (item) {
        if (!item) return;
        $state.go('app.billing.check', { id: item.id, check: item });
    };

    $scope.openAddNewCheckModal = () => {
        const modalInstance = $uibModal.open({
            templateUrl: 'admin/views/billing-new-manual-check-modal.html',
            size: 'md',
            controller: 'billingNewManualCheckModalCtrl',
            windowTopClass: "billing-new-manual-check-modal"
        });
        modalInstance.result.then(function (response) {
            if (response) {
                if (response.isError) {
                    toaster.pop("error", response.message);
                    $scope.isDataLoaded = true;
                }
                else {
                    toaster.pop("success", "New check added successfully");
                    $state.go('app.billing.check', { id: response.data.id, check: response.data })
                }
            }
            else {
                $scope.isDataLoaded = true;
            }
        }, function (rejection) {
            if (rejection !== "backdrop click") {
                toaster.pop("error", "Failed to open modal to add new check");
            }
            $scope.isDataLoaded = true;
        });
    }

    $scope.goToEdiFileUploader = function () {
        var controlData = {
            previousPageUrl: "app.billing.checks",

            // temporary implementation for the 'createDataToUpload' method
            createDataToUpload: function (scope) {
                var formData = new FormData();
                formData.append("file", scope.file, scope.file.name);
                return formData;
            },

            uploadPostUrl: "agencies/" + $rootScope.agencyId + "/upload_export_response",
            // historyGetUrl: "agencies/" + $rootScope.agencyId + "/table_file_upload",
            fileTypeName: "plain-text",
            // requiredInfoFields: [ "Name", "Status" ],
            caption: "Upload EDI files",
            subcaption: "Please make sure your EDI file contains the required information",
            css: "billing-page",
        };
        $state.go('app.billing.uploadEdi', { data: controlData });
    };

    $scope.exportTable = function () {
        $scope.loadingCSV = true;
        var rows = [];
        var titles = [
            'Check No.',
            'Contract',
            'Offices',
            'Check amount',
            'Applied amount',
            'Applied positive',
            'Applied negative',
            'Unapplied amount',
            'Unapplied positive',
            'Unapplied negative',
            'Visit Count',
            'Check Date',
            'Deposit Date',
            'Notes',
            'File URL'
        ];
        rows.push(titles);

        const allChecks = $scope.getFilteredSelectedForExport();
        allChecks.forEach(dataRow => {
            var csvRow = [];
            titles.forEach(title => {
                let toPush = "";
                switch (title) {
                    case "Check No.":
                        toPush = dataRow.checkNumber || '';
                        break;
                    case "Contract":
                        toPush = dataRow.contractTypeName ? dataRow.contractTypeName : '';
                        break;
                    case "Offices":
                        toPush = dataRow.officesNames ? dataRow.officesNames : '';
                        break;
                    case "Check amount":
                        toPush = $filter("centsToDollars")(dataRow.amount);
                        break;
                    case "Applied amount":
                        toPush = $filter("centsToDollars")(dataRow.appliedAmount);
                        break;
                    case "Applied positive":
                        toPush = $filter("centsToDollars")(dataRow.appliedPositiveAmount);
                        break;
                    case "Applied negative":
                        toPush = $filter("centsToDollars")(dataRow.appliedNegativeAmount);
                        break;
                    case "Unapplied amount":
                        toPush = $filter("centsToDollars")(dataRow.unappliedAmount);
                        break;
                    case "Unapplied positive":
                        toPush = $filter("centsToDollars")(dataRow.unappliedPositiveAmount);
                        break;
                    case "Unapplied negative":
                        toPush = $filter("centsToDollars")(dataRow.unappliedNegativeAmount);
                        break;
                    case "Visit Count":
                        toPush = dataRow.visitsCount;
                        break;
                    case "Check Date":
                        toPush = $filter("mfShortDate")(dataRow.checkDate) || '';
                        break;
                    case "Deposit Date":
                        toPush = $filter("mfShortDate")(dataRow.depositDate) || '';
                        break;
                    case "Notes":
                        toPush = dataRow.notes ? dataRow.notes : '';
                        break;
                    case "File URL":
                        toPush = dataRow.fileUrl || '';
                        break;
                    default:
                        break;
                }
                csvRow.push("\"" + toPush + "\"");
            });

            rows.push(csvRow);
        });

        var csvContent = "data:text/csv;charset=utf-8,";
        rows.forEach(function (rowArray) {
            var row = rowArray.join(",");
            csvContent += row + "\r\n";
        });

        var encodedUri = encodeURI(csvContent);
        var link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", getExportedFileName());
        document.body.appendChild(link);

        link.click();
        $scope.loadingCSV = false;
    };

    $scope.exportDetails = function (grouped) {
        const allChecks = $scope.getFilteredSelectedForExport();

        const url = wildcard(
            "agencies/:agencyId/agency_members/:agencyMemberId/check_batch_export",
            $rootScope.agencyId,
            $rootScope.agencyMemberId
        );

        if (allChecks.length === 0) {
            toaster.pop("error", "No checks selected", "Please select checks to export");
            return;
        }
        
        DatabaseApi.post(url, {
            grouped: grouped ? true : false,
            checkIds: allChecks.map(check => check.id)
        }).then((rsp) => {
            const fileUrl = rsp?.data?.url;
            if (!fileUrl) return;
      
            const downloadLink = document.createElement("a");
            downloadLink.setAttribute("href", fileUrl);
            document.body.appendChild(downloadLink);
            downloadLink.click();
          })
          .catch(() => {
            toaster.pop("error", "Something went wrong", "Couldn't get file")
          })
    }

    $scope.getFilteredSelectedForExport = function () {
        const selected = $scope.selectionLogic.getSelectedItems();
        const allChecks = $filter('filter')($scope.allChecks, $scope.globalFilter.val);
        const selectedIds = selected.map(row => row.id);
        const filtered = allChecks.filter(row => selectedIds.includes(row.id));

        return filtered.length === 0 ? allChecks : filtered;
    }

    var getExportedFileName = function () {
        var filename = "medflyt-export-batches"
            + $filter("date")(new Date($scope.pageManager.searchParams.from.value), "yyyy-MM-dd")
            + "-to-"
            + $filter("date")(new Date($scope.pageManager.searchParams.to.value), "yyyy-MM-dd")
            + ".csv";
        return filename;
    }

    $scope.removeCheckBatch = (checkId) => {
        SweetAlert.swal({
            title: 'Remove check',
            text: 'Are you sure you want to remove this check?',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3077EB',
            confirmButtonText: 'Yes, Remove',
            closeOnConfirm: true,
            closeOnCancel: true
        }, function (isConfirm) {
            if (isConfirm) {
                BillingChecksService.removeCheckById(checkId, {
                    agencyId: $rootScope.agencyId,
                    agencyMemberId: $rootScope.agencyMemberId
                }).then(() => {
                    toaster.pop("success", "Check removed successfully");
                    $scope.loadItems();
                }, (err) => {
                    toaster.pop("warning", "something went wrong", "Check wasn't removed");
                });
            }
        });
    }

    $scope.filterByCheckDate = () => {
        $scope.pageManager.searchParams.filterByDateType.value = 'CHECK_DATE';
    };

    $scope.filterByDepositDate = () => {
        $scope.pageManager.searchParams.filterByDateType.value = 'DEPOSIT_DATE';
    };

    $scope.isFilterByCheckDate = () => {
        return $scope.pageManager.searchParams.filterByDateType.value === 'CHECK_DATE';
    };

    $scope.isFilterByDepositDate = () => {
        return $scope.pageManager.searchParams.filterByDateType.value === 'DEPOSIT_DATE';
    };

    $scope.fromLabelText = () => {
        return $scope.isFilterByCheckDate() ? 'Check From' : 'Deposit From';
    };

    $scope.toLabelText = () => {
        return $scope.isFilterByCheckDate() ? 'Check To' : 'Deposit To';
    };

    initialize();
};